import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleAlert, X, FileText } from 'lucide-react';
import { PageLayout } from '../../components/organisms/PageLayout';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import '../../styles/new-base-resume.css';
import { emitSubscriptionRefresh } from '../../utils/subscriptionEvents';
import { track } from '../../utils/analytics';
import { showToast } from '../../utils/toastManager';

const breadcrumbItems = [
  { label: 'Resume Builder', href: '/resume-builder' },
  { label: 'New Base Resume' }
];

type ExperienceLevel = 'entry' | 'mid' | 'senior';
type DataSource = 'profile' | 'upload';

interface UserData {
  first_name: string | null;
  last_name: string | null;
}

interface WorkExperience {
  uid: string;
  employer_name: string;
  job_title: string;
  skills: string[] | null;
  description: string | null;
  achievements: string[] | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

interface Education {
  uid: string;
  institution_name: string;
  degree: string | null;
  field_of_study: string | null;
  graduation_date: string | null;
  description: string | null;
  grade: string | null;
  achievements: string[] | null;
  created_at: string;
  updated_at: string;
}

interface ProfileUserData {
  basic_info: any | null;
  preferences: any | null;
  experience: WorkExperience[] | null;
  volunteer_experience: any[] | null;
  certifications: any[] | null;
  education: Education[] | null;
  projects: any[] | null;
  urls: any[] | null;
  created_at: string;
  updated_at: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name: string;
  kanban_uid: string;
}

const NewBaseResume: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isBlankMode = searchParams.get('blank') === '1';
  const initialSource: DataSource =
    searchParams.get('source') === 'upload' ? 'upload' : 'profile';
  const [targetJobTitle, setTargetJobTitle] = useState('');
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>('entry');
  const [documentTitle, setDocumentTitle] = useState('');
  const [dataSource, setDataSource] = useState<DataSource>(initialSource);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [profileData, setProfileData] = useState<ProfileUserData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobUid, setSelectedJobUid] = useState<string>('');
  const [isJobDropdownOpen, setIsJobDropdownOpen] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);

  useEffect(() => {
    document.title = 'OfferFlow | New Base Resume';
  }, []);

  const MAX_PDF_SIZE_BYTES = 5 * 1024 * 1024;
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdfMime = file.type === 'application/pdf';
    const isPdfExt = file.name.toLowerCase().endsWith('.pdf');
    if (!isPdfMime && !isPdfExt) {
      showToast('Please upload a PDF file (.pdf).', 'error');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      showToast(`PDF is too large (max 5 MB, got ${(file.size / 1024 / 1024).toFixed(1)} MB).`, 'error');
      e.target.value = '';
      return;
    }
    setUploadedFile(file);
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await api.get<UserData>(API_ENDPOINTS.me);
        setUserData(data);
      } catch (err) {
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setProfileLoading(true);
        const data = await api.get<ProfileUserData>(API_ENDPOINTS.users.getByUid);
        setProfileData(data);
      } catch (err) {
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfileData();
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setJobsLoading(true);
        const jobsData = await api.get<Job[]>(API_ENDPOINTS.jobs.myJobs);
        if (Array.isArray(jobsData)) {
          setJobs(jobsData);
        }
      } catch (err) {
      } finally {
        setJobsLoading(false);
      }
    };
    fetchJobs();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.form-field__dropdown-wrapper')) {
        setIsJobDropdownOpen(false);
      }
    };

    if (isJobDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isJobDropdownOpen]);

  useEffect(() => {
    if (targetJobTitle && userData) {
      const firstName = userData.first_name || '';
      const lastName = userData.last_name || '';
      const namePart = [firstName, lastName].filter(Boolean).join(' ');
      const newTitle = namePart ? `${targetJobTitle} - ${namePart} - Resume` : `${targetJobTitle} - Resume`;
      setDocumentTitle(newTitle);
    } else if (targetJobTitle && !userData) {
      setDocumentTitle(`${targetJobTitle} - Resume`);
    } else if (!targetJobTitle) {
      setDocumentTitle('');
    }
  }, [targetJobTitle, userData]);

  const handleContinue = async () => {
    if (dataSource === 'profile') {
      try {
        setLoading(true);
        setError(null);
        
        const payload: any = {
          job_title: targetJobTitle,
          experience_level: experienceLevel,
          title: documentTitle,
          source: 'profile'
        };

        if (selectedJobUid) {
          payload.job_uid = selectedJobUid;
        }

        const response = await api.post<any>(
          API_ENDPOINTS.cvs.create,
          payload
        );

        const cvUid = response?.uid;
        if (!cvUid) {
          throw new Error('CV UID not found in response');
        }

        track('resume_created', { source: 'profile', experience_level: experienceLevel });
        emitSubscriptionRefresh();

        navigate(`/resume-builder/editor/${cvUid}`);
      } catch (err: any) {
        setError(err?.message || 'Failed to create resume. Please try again.');
        setLoading(false);
      }
    } else {
      if (!uploadedFile) {
        setError('Please upload a PDF file');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', uploadedFile);
        
        const cvData = {
          job_title: targetJobTitle,
          experience_level: experienceLevel,
          title: documentTitle,
          source: 'cv' as const,
          job_uid: selectedJobUid || null,
        };
        formData.append('cv_data', JSON.stringify(cvData));

        const response = await api.postFormData<any>(
          API_ENDPOINTS.cvs.createWithFile,
          formData
        );

        const cvUid = response?.uid;
        if (!cvUid) {
          throw new Error('CV UID not found in response');
        }

        track('resume_created', { source: 'upload', experience_level: experienceLevel });
        emitSubscriptionRefresh();

        navigate(`/resume-builder/editor/${cvUid}`);
      } catch (err: any) {
        setError(err?.message || 'Failed to create resume. Please try again.');
        setLoading(false);
      }
    }
  };

  const isFormValid = targetJobTitle && experienceLevel && documentTitle && (dataSource === 'profile' || uploadedFile);

  const handleCreateBlank = async () => {
    const title = documentTitle || 'Untitled Resume';
    try {
      setLoading(true);
      setError(null);
      const response = await api.post<any>(API_ENDPOINTS.cvs.create, {
        job_title: targetJobTitle || 'New Resume',
        experience_level: experienceLevel,
        title,
        source: 'blank',
      });
      const cvUid = response?.uid;
      if (!cvUid) throw new Error('CV UID not found');
      track('resume_created', { source: 'blank', experience_level: experienceLevel });
      emitSubscriptionRefresh();
      navigate(`/resume-builder/editor/${cvUid}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to create resume.');
      setLoading(false);
    }
  };

  if (isBlankMode) {
    return (
      <PageLayout breadcrumbItems={breadcrumbItems}>
        <div className="new-base-resume">
          <div className="new-base-resume__container">
            <div className="new-base-resume__left">
              <div className="new-base-resume__intro">
                <span className="new-base-resume__label">Create a new</span>
                <h1 className="new-base-resume__title">Blank CV</h1>
                <p className="new-base-resume__description">
                  Start with a completely empty resume. You'll fill in all the details manually in the editor.
                </p>
                <button
                  className="new-base-resume__continue-btn"
                  onClick={handleCreateBlank}
                  disabled={loading}
                >
                  {loading ? 'Creating…' : 'Create & Open Editor'}
                </button>
                {error && (
                  <p style={{ color: '#dc2626', marginTop: '8px', fontSize: '13px' }}>{error}</p>
                )}
              </div>
            </div>
            <div className="new-base-resume__right">
              <form className="new-base-resume__form">
                <div className="form-field">
                  <div className="form-field__header">
                    <label className="form-field__label">Document Title</label>
                    <span className="form-field__optional">Optional</span>
                  </div>
                  <p className="form-field__hint">How you'll find this resume in OfferFlow.</p>
                  <input
                    type="text"
                    className="form-field__input"
                    placeholder="e.g. Frontend Dev Resume"
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <div className="new-base-resume">
        <div className="new-base-resume__container">
          <div className="new-base-resume__left">
            <div className="new-base-resume__intro">
              <span className="new-base-resume__label">Create a new</span>
              <h1 className="new-base-resume__title">Base Resume</h1>
              <p className="new-base-resume__description">
                Just a few quick questions to help us customize your resume for the perfect job title and experience level. You can always change these settings later.
              </p>
              <button
                className="new-base-resume__continue-btn"
                onClick={handleContinue}
                disabled={!isFormValid || loading}
              >
                {loading ? 'Creating...' : 'Continue'}
              </button>
              {error && (
                <p className="new-base-resume__error" style={{ color: '#dc2626', marginTop: '8px', fontSize: '13px' }}>
                  {error}
                </p>
              )}
              <p className="new-base-resume__hint">Fill out all required fields</p>
              <button
                type="button"
                style={{ background: 'none', border: 'none', padding: 0, marginTop: '12px', fontSize: '12px', color: '#6b7280', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/resume-builder/new-base-resume?blank=1')}
              >
                or create a blank CV
              </button>
            </div>
          </div>

          <div className="new-base-resume__right">
            <form className="new-base-resume__form">
              <div className="form-field">
                <div className="form-field__header">
                  <label className="form-field__label">Target Job Title</label>
                  <span className="form-field__required">Required</span>
                </div>
                <p className="form-field__hint">What job title are you targeting with this resume?</p>
                <input
                  type="text"
                  className="form-field__input"
                  placeholder="Keyword"
                  value={targetJobTitle}
                  onChange={(e) => setTargetJobTitle(e.target.value)}
                />
              </div>

              <div className="form-field">
                <div className="form-field__header">
                  <label className="form-field__label">Link to Job</label>
                  <span className="form-field__optional">Optional</span>
                </div>
                <p className="form-field__hint">Select a job from your tracker to link this resume</p>
                <div className="form-field__dropdown-wrapper">
                  <button
                    type="button"
                    className="form-field__dropdown-btn"
                    onClick={() => setIsJobDropdownOpen(!isJobDropdownOpen)}
                  >
                    <span className={selectedJobUid ? '' : 'form-field__dropdown-placeholder'}>
                      {selectedJobUid
                        ? (() => {
                            const selectedJob = jobs.find(j => j.uid === selectedJobUid);
                            return selectedJob
                              ? `${selectedJob.job_title} | ${selectedJob.company_name}`
                              : 'Select a job';
                          })()
                        : 'Select a job (optional)'}
                    </span>
                    <div className="form-field__dropdown-actions">
                      {selectedJobUid && (
                        <button
                          type="button"
                          className="form-field__dropdown-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobUid('');
                            setIsJobDropdownOpen(false);
                          }}
                          title="Remove job link"
                        >
                          <X size={14} />
                        </button>
                      )}
                      <span className="form-field__dropdown-arrow">▾</span>
                    </div>
                  </button>
                  {isJobDropdownOpen && (
                    <div className="form-field__dropdown-menu">
                      {jobsLoading ? (
                        <div className="form-field__dropdown-item form-field__dropdown-item--disabled">
                          Loading jobs...
                        </div>
                      ) : jobs.length === 0 ? (
                        <div className="form-field__dropdown-item form-field__dropdown-item--disabled">
                          No jobs found. Add jobs to your tracker first.
                        </div>
                      ) : (
                        jobs.map((job) => (
                          <button
                            key={job.uid}
                            type="button"
                            className={`form-field__dropdown-item ${selectedJobUid === job.uid ? 'form-field__dropdown-item--selected' : ''}`}
                            onClick={() => {
                              setSelectedJobUid(job.uid);
                              setIsJobDropdownOpen(false);
                            }}
                          >
                            {job.job_title} | {job.company_name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-field">
                <div className="form-field__header">
                  <label className="form-field__label">Experience Level</label>
                  <span className="form-field__required">Required</span>
                </div>
                <p className="form-field__hint">What experience level are you targeting?</p>
                <div className="form-field__radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="experience"
                      value="entry"
                      checked={experienceLevel === 'entry'}
                      onChange={() => setExperienceLevel('entry')}
                    />
                    <span className="radio-option__label">Entry - 0-2 years of exp.</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="experience"
                      value="mid"
                      checked={experienceLevel === 'mid'}
                      onChange={() => setExperienceLevel('mid')}
                    />
                    <span className="radio-option__label">Mid Level - 2-5 years of exp.</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="experience"
                      value="senior"
                      checked={experienceLevel === 'senior'}
                      onChange={() => setExperienceLevel('senior')}
                    />
                    <span className="radio-option__label">Senior - 5+ years of exp.</span>
                  </label>
                </div>
              </div>

              <div className="form-field">
                <div className="form-field__header">
                  <label className="form-field__label">Document Title</label>
                  <span className="form-field__required">Required</span>
                </div>
                <p className="form-field__hint">
                  This is used to find your resume in OfferFlow. It is not the file name.
                </p>
                <input
                  type="text"
                  className="form-field__input"
                  placeholder="Document title"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                />
              </div>

              <div className="form-field">
                <div className="form-field__header">
                  <label className="form-field__label">Resume Data Source</label>
                  <span className="form-field__required">Required</span>
                </div>
                <p className="form-field__hint">
                  Looks like you have data in your OfferFlow Profile, so we've pre-selected it as a source for this resume. If you don't have enough data in your profile, you can import your resume from LinkedIn or upload a PDF file.
                </p>
                
                <div className="data-source-tabs">
                  <button
                    type="button"
                    className={`data-source-tab ${dataSource === 'profile' ? 'data-source-tab--active' : ''}`}
                    onClick={() => setDataSource('profile')}
                  >
                    OfferFlow Profile
                  </button>
                  <button
                    type="button"
                    className={`data-source-tab ${dataSource === 'upload' ? 'data-source-tab--active' : ''}`}
                    onClick={() => setDataSource('upload')}
                  >
                    Resume Upload
                  </button>
                </div>

                {dataSource === 'profile' && (
                  <div className="data-source-content">
                    {profileLoading ? (
                      <div className="profile-summary">
                        <div className="profile-summary__title">OfferFlow Profile Summary</div>
                        <div className="profile-summary__content">Loading profile data...</div>
                      </div>
                    ) : (() => {
                      const experiences = profileData?.experience || null;
                      const educations = profileData?.education || null;
                      const hasExperience = experiences && experiences.length > 0;
                      const hasEducation = educations && educations.length > 0;

                      if (!hasExperience && !hasEducation) {
                        return (
                          <div className="profile-summary profile-summary--empty">
                            <div className="profile-summary__title">OfferFlow Profile Summary</div>
                            <div className="profile-summary__empty-state">
                              <CircleAlert className="profile-summary__empty-icon" size={48} />
                              <div className="profile-summary__empty-description">
                                Your OfferFlow profile doesn't have enough data yet. You can fill your CV manually in the editor after creating it.
                              </div>
                              <button
                                type="button"
                                className="resume-type-card__button"
                                style={{ marginTop: '12px' }}
                                disabled={!documentTitle || loading}
                                onClick={async () => {
                                  if (!documentTitle) return;
                                  try {
                                    setLoading(true);
                                    setError(null);
                                    const response = await api.post<any>(API_ENDPOINTS.cvs.create, {
                                      job_title: targetJobTitle || 'New Resume',
                                      experience_level: experienceLevel,
                                      title: documentTitle,
                                      source: 'blank',
                                    });
                                    const cvUid = response?.uid;
                                    if (!cvUid) throw new Error('CV UID not found');
                                    track('resume_created', { source: 'blank', experience_level: experienceLevel });
                                    emitSubscriptionRefresh();
                                    navigate(`/resume-builder/editor/${cvUid}`);
                                  } catch (err: any) {
                                    setError(err?.message || 'Failed to create resume.');
                                    setLoading(false);
                                  }
                                }}
                              >
                                {loading ? 'Creating…' : 'Create Blank CV →'}
                              </button>
                              {!documentTitle && (
                                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                                  Fill in Document Title above to enable
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }

                      const experienceLabel = hasExperience
                        ? `${experiences.length} Work Experience${experiences.length !== 1 ? 's' : ''} at `
                        : null;
                      const experienceCompanies = hasExperience
                        ? experiences.map(exp => exp.employer_name).join(', ')
                        : null;

                      const educationLabel = hasEducation
                        ? `${educations.length} Education${educations.length !== 1 ? 's' : ''} at `
                        : null;
                      const educationInstitutions = hasEducation
                        ? educations.map(edu => edu.institution_name).join(', ')
                        : null;

                      return (
                        <div className="profile-summary">
                          <div className="profile-summary__title">OfferFlow Profile Summary</div>
                          <div className="profile-summary__content">
                            {hasExperience && (
                              <>
                                <strong>{experienceLabel}</strong>{experienceCompanies}
                                {hasEducation && <br />}
                              </>
                            )}
                            {hasEducation && (
                              <>
                                <strong>{educationLabel}</strong>{educationInstitutions}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {dataSource === 'upload' && (
                  <div className="data-source-content">
                    {uploadedFile ? (
                      <div className="uploaded-file">
                        <FileText size={24} className="uploaded-file__icon" />
                        <div className="uploaded-file__info">
                          <span className="uploaded-file__label">File name:</span>
                          <span className="uploaded-file__name">{uploadedFile.name}</span>
                        </div>
                        <button
                          type="button"
                          className="uploaded-file__remove"
                          onClick={() => setUploadedFile(null)}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="file-upload">
                        <input
                          type="file"
                          id="resume-upload"
                          accept=".pdf"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="resume-upload" className="file-upload__label">
                          <div className="file-upload__icon">☁</div>
                          <div className="file-upload__text">Click to upload PDF</div>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="form-continue-btn"
                onClick={handleContinue}
                disabled={!isFormValid}
              >
                Continue
              </button>
            </form>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default NewBaseResume;

